/**
 * TodoMenu — the one dropdown of the CD-25 Todo container.
 *
 * The design handoff (§3) is explicit that Type · Call · Workflow · Agent ·
 * Record are "the same object; only width and header differ" — so this is one
 * component with a `width` and an optional `header`/`createRow`, not five
 * lookalikes. It also adds the behaviours the HTML specimen only faked:
 * click-outside to dismiss, Esc to close, arrow-key roving focus, and the
 * `listbox`/`option` semantics.
 *
 * Anchoring goes through the repo's canonical `PopoverPortal` +
 * `usePopoverPosition`, NOT a CSS-absolute box in the cell. The design's
 * `top: calc(100% + 5px)` describes where the menu should APPEAR, but the
 * compact paper is `overflow: hidden` with a content-driven height, so an
 * in-flow menu is clipped at the paper edge — on a short to-do, four of the
 * nine type rows are simply unreachable, and the client roster is worse.
 * Portaling to document.body escapes every overflow ancestor (that primitive's
 * whole reason for existing) and gives us flip-up-when-tight for free.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './todoContainer.module.css';
import { PopoverPortal, usePopoverPosition } from '../shared/InlineEdit/popoverPortal';

export interface TodoMenuOption {
  /** Stable key + the value handed back on pick. */
  value: string | number | null;
  label: string;
  /** Right-aligned faint meta ("v3 · 9 steps", "Operations", "30m"). */
  meta?: string | null;
  disabled?: boolean;
  title?: string;
  /** Optional section this row belongs to ("Project calls" / "Your own"). A
   *  kicker is emitted whenever it CHANGES between consecutive rows, so the
   *  caller controls grouping purely by the order it passes options in — the
   *  array stays flat, which keeps the roving-focus index and the type-ahead
   *  filter working unchanged. Rows with no `group` render as before. */
  group?: string | null;
}

export interface TodoMenuProps {
  open: boolean;
  onClose: () => void;
  /** The chip the menu hangs off. Required — the menu is portaled, so it has
   *  no DOM parent to measure. */
  anchorRef: React.RefObject<HTMLElement>;
  options: TodoMenuOption[];
  /** The currently-selected value — marked with a diamond in the accent. */
  value: string | number | null;
  onPick: (value: string | number | null) => void;
  /** Uppercase kicker header ("Link a workflow", "Assign an agent"). */
  header?: string;
  /** Indigo create row pinned to the bottom ("＋ Create a new workflow"). */
  createLabel?: string;
  onCreate?: () => void;
  /** Selection accent. `paprika` is the agent picker; everything else indigo. */
  accent?: 'indigo' | 'paprika';
  /** `links` = the call-link picker: bigger rows, and two columns on a large screen, because it lists every person's calls. */
  width?: 'default' | 'wide' | 'agent' | 'links';
  /** Shown in place of the rows when `options` is empty — honest, never blank. */
  emptyLabel?: string;
  /** Show a sticky type-ahead above the rows. Set it for any list that can run
   *  long (clients, projects) — CLAUDE.md's SearchableSelect rule exists
   *  because a picker with hundreds of unfiltered rows is unusable. */
  filterPlaceholder?: string;
}

/** Where the call-link picker goes to two columns: keep in step with `todoContainer.module.css`. */
const LINKS_TWO_COLUMNS = '(min-width: 1100px)';

const TodoMenu: React.FC<TodoMenuProps> = ({
  open,
  onClose,
  anchorRef,
  options,
  value,
  onPick,
  header,
  createLabel,
  onCreate,
  accent = 'indigo',
  width = 'default',
  emptyLabel = 'Nothing to pick yet',
  filterPlaceholder,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [active, setActive] = useState(0);
  const [query, setQuery] = useState('');
  // Width hints let the positioner keep a wide menu on-screen near the right
  // edge; the height hint drives the flip-up-when-tight decision.
  const twoColumns = width === 'links' && typeof window !== 'undefined' && !!window.matchMedia?.(LINKS_TWO_COLUMNS).matches;
  // The long list takes the room it has, on whichever side of its button has more, so
  // its last row can always be scrolled to.
  const linksHeight = useMemo(() => {
    const tallest = twoColumns ? 520 : 420;
    const r = open && width === 'links' ? anchorRef.current?.getBoundingClientRect() : null;
    return r ? Math.max(200, Math.min(tallest, Math.max(window.innerHeight - r.bottom, r.top) - 16)) : tallest;
  }, [open, width, twoColumns, anchorRef]);
  const coords = usePopoverPosition(
    anchorRef,
    open,
    width === 'agent' ? 236 : width === 'links' ? linksHeight : 264,
    width === 'default' ? 190 : width === 'wide' ? 250 : width === 'links' ? (twoColumns ? 720 : 360) : 268,
  );

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    // The group matches too, so typing a person's name finds all of their calls.
    return options.filter((o) => o.label.toLowerCase().includes(q) || (o.meta || '').toLowerCase().includes(q) || (o.group || '').toLowerCase().includes(q));
  }, [options, query]);

  // The rows under each kicker, so the two-column list keeps a person's calls together.
  const runs = useMemo(() => {
    const out: Array<{ group: string | null; rows: Array<{ o: TodoMenuOption; i: number }> }> = [];
    shown.forEach((o, i) => {
      const group = o.group || null;
      const last = out[out.length - 1];
      if (last && last.group === group) last.rows.push({ o, i });
      else out.push({ group, rows: [{ o, i }] });
    });
    return out;
  }, [shown]);

  // Reset the type-ahead each time the menu opens — a stale query would hide
  // rows the user never filtered for.
  useEffect(() => { if (open) setQuery(''); }, [open]);

  // FOCUS THE MENU ON OPEN. Without this the arrow-key / Enter handling below
  // never fires: `onKeyDown` on a `tabIndex={-1}` div only sees keys when that
  // div (or a descendant) holds focus, and the chip that opened it keeps focus
  // by default — so the documented roving focus was dead code. When the
  // type-ahead is present it takes focus instead (typing is the primary
  // interaction there) and forwards the navigation keys.
  useEffect(() => {
    if (!open || filterPlaceholder) return;
    ref.current?.focus();
  }, [open, filterPlaceholder]);

  // Open on the selected row so arrow-keys start where the eye is.
  useEffect(() => {
    if (!open) return;
    const i = shown.findIndex((o) => o.value === value);
    setActive(i >= 0 ? i : 0);
  }, [open, shown, value]);

  // Esc closes. Click-outside is the portal's job (it knows its own bounds and
  // ignores the anchor), so we don't hand-roll a second one that would fight it.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);

  const move = useCallback((delta: number) => {
    setActive((cur) => {
      const n = shown.length;
      if (!n) return 0;
      let next = cur;
      for (let step = 0; step < n; step += 1) {
        next = (next + delta + n) % n;
        if (!shown[next]?.disabled) return next;
      }
      return cur;
    });
  }, [shown]);

  const cls = [
    styles.menu,
    width === 'wide' ? styles.menuWide : '',
    width === 'agent' ? styles.menuAgent : '',
    width === 'links' ? styles.menuLinks : '',
  ].filter(Boolean).join(' ');

  const diamond = accent === 'paprika' ? styles.diamondPaprika : styles.diamondIndigo;

  // One handler, used by the menu itself AND by the type-ahead input — whichever
  // holds focus. Space is only a "pick" when the menu has focus; inside the
  // filter it must stay a space character.
  const onNavKey = (e: React.KeyboardEvent, allowSpace: boolean) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); move(1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); move(-1); }
    else if (e.key === 'Enter' || (allowSpace && e.key === ' ')) {
      e.preventDefault();
      const opt = shown[active];
      if (opt && !opt.disabled) { onPick(opt.value); onClose(); }
    }
  };

  return (
    <PopoverPortal
      open={open}
      coords={coords}
      onClickOutside={onClose}
      ignoreRefs={[anchorRef]}
      animate
    >
    <div
      ref={ref}
      className={cls}
      style={width === 'links' ? { maxHeight: linksHeight } : undefined}
      role="listbox"
      tabIndex={-1}
      onKeyDown={(e) => onNavKey(e, true)}
    >
      {header && <div className={styles.menuHead}>{header}</div>}
      {filterPlaceholder && (
        <input
          className={styles.menuFilter}
          value={query}
          autoFocus
          placeholder={filterPlaceholder}
          onChange={(e) => setQuery(e.target.value)}
          // Stop it here: the menu around it listens too, and one press would move two rows, or pick twice.
          onKeyDown={(e) => { e.stopPropagation(); onNavKey(e, false); }}
          aria-label={filterPlaceholder}
        />
      )}
      {options.length === 0 && <div className={styles.menuHead}>{emptyLabel}</div>}
      {options.length > 0 && shown.length === 0 && (
        <div className={styles.menuHead}>No match for “{query}”</div>
      )}
      <div className={width === 'links' ? styles.menuColumns : undefined}>
      {runs.map((run) => (
        <div key={`${run.group ?? 'none'}-${run.rows[0].i}`} className={styles.menuRun}>
        {run.group && <div className={styles.menuHead}>{run.group}</div>}
        {run.rows.map(({ o, i }) => (
        <button
          key={`${o.value ?? 'none'}-${i}`}
          type="button"
          role="option"
          aria-selected={o.value === value}
          disabled={o.disabled}
          title={o.title}
          className={styles.menuRow}
          style={i === active && !o.disabled ? { background: 'var(--rule-soft)' } : undefined}
          onMouseEnter={() => !o.disabled && setActive(i)}
          onClick={() => { onPick(o.value); onClose(); }}
        >
          <span className={o.value === value ? diamond : styles.diamondSpacer} />
          <span className={styles.menuRowLabel}>{o.label}</span>
          {o.meta && <span className={styles.menuRowMeta}>{o.meta}</span>}
        </button>
        ))}
        </div>
      ))}
      </div>
      {createLabel && onCreate && (
        <button type="button" className={styles.menuCreate} onClick={() => { onCreate(); onClose(); }}>
          ＋ {createLabel}
        </button>
      )}
    </div>
    </PopoverPortal>
  );
};

export default TodoMenu;
